import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { Home, ArrowLeft, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";

const NotFound = () => {
  const location = useLocation();
  const { isFrench, getLocalePath } = useI18n();

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
    
    // Set noindex for 404 pages
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', 'noindex, nofollow');
    
    // Update document title
    document.title = isFrench ? "Page non trouvée | MTLI Group" : "Page Not Found | MTLI Group";
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', isFrench 
      ? "Cette page n'existe pas. Retournez à l'accueil pour trouver ce que vous cherchez."
      : "This page doesn't exist. Return to the homepage to find what you're looking for."
    );
    
    return () => {
      // Remove noindex when leaving 404 page
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta) {
        robotsMeta.remove();
      }
    };
  }, [location.pathname, isFrench]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow flex items-center justify-center bg-muted py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            {/* 404 Graphic */}
            <div className="mb-8">
              <span className="text-8xl md:text-9xl font-bold text-primary/20">404</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {isFrench ? "Page non trouvée" : "Page Not Found"}
            </h1>
            
            <p className="text-lg text-muted-foreground mb-8">
              {isFrench 
                ? "La page que vous cherchez n'existe pas ou a été déplacée."
                : "The page you're looking for doesn't exist or has been moved."
              }
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <Link to={getLocalePath("/")}>
                  <Home className="mr-2 h-5 w-5" />
                  {isFrench ? "Retour à l'accueil" : "Back to Home"}
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to={getLocalePath("/contact")}>
                  <Search className="mr-2 h-5 w-5" />
                  {isFrench ? "Contactez-nous" : "Contact Us"}
                </Link>
              </Button>
            </div>
            
            {/* Popular Links */}
            <div className="mt-12 pt-8 border-t border-border">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                {isFrench ? "Liens populaires" : "Popular Links"}
              </h2>
              <div className="flex flex-wrap justify-center gap-4">
                <Link 
                  to={getLocalePath("/services")} 
                  className="text-primary hover:underline"
                >
                  {isFrench ? "Services" : "Services"}
                </Link>
                <Link 
                  to={getLocalePath("/industries")} 
                  className="text-primary hover:underline"
                >
                  {isFrench ? "Industries" : "Industries"}
                </Link>
                <Link 
                  to={getLocalePath("/projects")} 
                  className="text-primary hover:underline"
                >
                  {isFrench ? "Projets" : "Projects"}
                </Link>
                <Link 
                  to={getLocalePath("/book-a-call")} 
                  className="text-primary hover:underline"
                >
                  {isFrench ? "Réserver un appel" : "Book a Call"}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default NotFound;
